import { useEffect, useState } from 'react';

/**
 * @file Full-screen loading overlay. Two sources feed it:
 *  - GameCanvas's own `status` prop (`'booting'` = engine/renderer not up
 *    yet, no bus to listen to at all).
 *  - The engine's own bus once it exists: `'level:progress'` (Engine.js
 *    emits `{t, label}` — `t` is the 0..1 fraction; some plan drafts call
 *    this field `p`, so both are read defensively), `'level:loading'`
 *    (a fresh `setLevel()` started), `'level:ready'` (hide).
 *
 * Hidden on `'error'` so the plain status readout in PlayRoute (rendered
 * underneath, at a lower z-index) is reachable instead of being covered by
 * a loading screen stuck at some half-finished percentage forever.
 */
/**
 * @param {Object} props
 * @param {any} props.engine
 * @param {string} props.status
 * @param {boolean} [props.holding=false] PlayRoute keeps the screen up past
 *   `level:ready` while the gameplay systems install — the city's one-off
 *   car-archetype bake (~0.4 s) hides under it instead of freezing the
 *   first playable frame.
 */
export default function LoadingScreen({ engine, status, holding = false }) {
  const [progress, setProgress] = useState({ p: 0, label: '' });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    setProgress({ p: 0, label: '' });

    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    const offProgress = bus.on('level:progress', (payload) => {
      const p = typeof payload?.t === 'number' ? payload.t : typeof payload?.p === 'number' ? payload.p : 0;
      setProgress({ p, label: payload?.label ?? '' });
    });
    const offLoading = bus.on('level:loading', () => {
      setReady(false);
      setProgress({ p: 0, label: 'loading…' });
    });
    const offReady = bus.on('level:ready', () => setReady(true));

    return () => {
      offProgress?.();
      offLoading?.();
      offReady?.();
    };
  }, [engine]);

  const visible = status !== 'error' && (status === 'booting' || !ready || holding);
  if (!visible) return null;

  const held = ready && holding;
  const pct = held ? 100 : Math.round(Math.min(1, Math.max(0, progress.p)) * 100);
  const label = held
    ? 'starting traffic…'
    : status === 'booting' && !engine ? 'starting engine…' : progress.label || 'loading…';

  return (
    <div className="pointer-events-auto absolute inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-void">
      <div className="gta-outline select-none font-display text-3xl text-gold">HARBOURSIDE</div>
      <div className="select-none font-hud text-[12px] uppercase tracking-[0.34em] text-hud-faint">{label}</div>
      <div className="h-1.5 w-64 overflow-hidden rounded-full bg-steel">
        <div
          className="h-full rounded-full bg-gold transition-[width] duration-200 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="select-none font-mono text-[10px] text-hud-faint">{pct}%</div>
    </div>
  );
}
